import { supabase } from "@/integrations/supabase/client";
import { BuyerList, BuyerListItem } from "@/types/buyerSearch";

export const buyerListService = {
  createList: async (
    sellerId: string, 
    listName: string, 
    description?: string
  ): Promise<{ data: BuyerList | null; error?: string }> => {
    try {
      // Use any type for RPC calls until types are updated
      const { data, error } = await (supabase as any).rpc('create_buyer_list', {
        p_seller_id: sellerId,
        p_list_name: listName,
        p_description: description
      });

      if (error) {
        console.error('Error creating buyer list:', error);
        return { data: null, error: error.message };
      }

      return { data: data && data.length > 0 ? data[0] : null };
    } catch (error) {
      console.error('Error in buyerListService.createList:', error);
      return { data: null, error: 'Failed to create buyer list' };
    }
  },

  updateList: async (
    listId: string, 
    updates: Partial<BuyerList>
  ): Promise<{ success: boolean; error?: string }> => {
    try {
      const { error } = await (supabase as any).rpc('update_buyer_list', {
        p_list_id: listId,
        p_list_name: updates.list_name,
        p_description: updates.description
      });

      if (error) {
        console.error('Error updating buyer list:', error);
        return { success: false, error: error.message };
      }

      return { success: true };
    } catch (error) {
      console.error('Error in buyerListService.updateList:', error);
      return { success: false, error: 'Failed to update buyer list' };
    }
  },

  deleteList: async (listId: string): Promise<{ success: boolean; error?: string }> => {
    try {
      const { error } = await (supabase as any).rpc('delete_buyer_list', {
        p_list_id: listId
      });

      if (error) {
        console.error('Error deleting buyer list:', error);
        return { success: false, error: error.message };
      }

      return { success: true };
    } catch (error) {
      console.error('Error in buyerListService.deleteList:', error);
      return { success: false, error: 'Failed to delete buyer list' };
    }
  },

  getSellerLists: async (sellerId: string): Promise<{ data: BuyerList[]; error?: string }> => {
    try {
      const { data, error } = await (supabase as any).rpc('get_seller_buyer_lists', {
        p_seller_id: sellerId
      });

      if (error) {
        console.error('Error fetching seller lists:', error);
        return { data: [], error: error.message };
      }

      return { data: data || [] };
    } catch (error) {
      console.error('Error in buyerListService.getSellerLists:', error);
      return { data: [], error: 'Failed to fetch buyer lists' };
    }
  },

  getListDetails: async (listId: string): Promise<{ data: BuyerList | null; error?: string }> => {
    try {
      const { data, error } = await (supabase as any).rpc('get_buyer_list_details', {
        p_list_id: listId
      });

      if (error) {
        console.error('Error fetching list details:', error);
        return { data: null, error: error.message };
      }

      return { data: data && data.length > 0 ? data[0] : null };
    } catch (error) {
      console.error('Error in buyerListService.getListDetails:', error);
      return { data: null, error: 'Failed to fetch list details' };
    }
  },

  addBuyerToList: async (
    listId: string, 
    buyerId: string, 
    notes?: string
  ): Promise<{ success: boolean; error?: string }> => {
    try {
      const { error } = await (supabase as any).rpc('add_buyer_to_list', {
        p_list_id: listId,
        p_buyer_id: buyerId,
        p_notes: notes
      });

      if (error) {
        if (error.message?.includes('duplicate') || error.message?.includes('unique')) {
          return { success: false, error: 'Buyer is already in this list' };
        }
        console.error('Error adding buyer to list:', error);
        return { success: false, error: error.message };
      }

      return { success: true };
    } catch (error) {
      console.error('Error in buyerListService.addBuyerToList:', error);
      return { success: false, error: 'Failed to add buyer to list' };
    }
  },

  removeBuyerFromList: async (
    listId: string, 
    buyerId: string
  ): Promise<{ success: boolean; error?: string }> => {
    try {
      const { error } = await (supabase as any).rpc('remove_buyer_from_list', {
        p_list_id: listId,
        p_buyer_id: buyerId
      });

      if (error) {
        console.error('Error removing buyer from list:', error);
        return { success: false, error: error.message };
      }

      return { success: true };
    } catch (error) {
      console.error('Error in buyerListService.removeBuyerFromList:', error);
      return { success: false, error: 'Failed to remove buyer from list' };
    }
  },

  getBuyersInList: async (listId: string): Promise<{ data: BuyerListItem[]; error?: string }> => {
    try {
      const { data, error } = await (supabase as any).rpc('get_buyers_in_list', {
        p_list_id: listId
      });

      if (error) {
        console.error('Error fetching buyers in list:', error);
        return { data: [], error: error.message };
      }

      return { data: data || [] };
    } catch (error) {
      console.error('Error in buyerListService.getBuyersInList:', error);
      return { data: [], error: 'Failed to fetch buyers in list' };
    }
  }
};